import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import connect from '@app/utils/connect';
import { Client, User } from '@app/types/api';
import { Typography, Flex, Button } from '@app/ui';
import SocBenchHighlightsContainer from './SocBenchHighlightsContainer';
import SocBenchHighlights from './SocBenchHighlights';
import { SocialBenchmarkingProvider } from './SocialBenchmarkingContext';
import audienceActions from '@app/actions/audience';
import SocBenchFilters from './SocBenchFilters';
import { HighlightResults } from '@app/actions/audience/getSocialBenchmarkingAccountGroups';
import SocialBenchmarkingChart from './SocialBenchmarkingChart';
import SocialBenchmarkingTable from './SocialBenchmarkingTable';
import SocialBenchmarkingFavorites from './SocialBenchmarkingFavorites';
import { ControlOutlined } from '@ant-design/icons';
import sportsApi from '@app/utils/sportsApi';
const { Title } = Typography;

interface SocialBenchmarkingProps {
    user: User;
    client: Client;
    socialBenchmarking: {
        isLoadingLeagues: boolean;
        favoritesList: any | null;
        leagues: any[];
        accountGroups: any[];
        isLoading: boolean;
        highlights: HighlightResults;
        periodStartDate: string;
        periodEndDate: string;
        hasNoData: boolean;
    };
    getLeaguesAndFavorites: (...args: any[]) => Promise<void>;
    getSocialBenchmarkingAccountGroups: (filters: any) => Promise<void>;
    sendNotifications: (message: string, options: any) => void;
}

const SocialBenchmarking: React.FC<SocialBenchmarkingProps> = ({
    socialBenchmarking,
    client,
    user,
    getSocialBenchmarkingAccountGroups,
    getLeaguesAndFavorites,
    sendNotifications,
}) => {
    const { t } = useTranslation('benchmarking');
    const {
        accountGroups,
        leagues,
        isLoading,
        isLoadingLeagues,
        highlights,
        favoritesList,
        // hasIncompleteData,
        // hasNoData,
    } = socialBenchmarking;

    useEffect(() => {
        if (favoritesList) return;
        (async () => {
            if (isLoadingLeagues) {
                await getLeaguesAndFavorites();
            }
        })();
    }, []);

    const [isFavoritesOpen, setIsFavoritesOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const handleSaveFavorites = useCallback(
        async (toAdd: number[], toRemove: number[]) => {
            try {
                if (!favoritesList) return;
                setIsSaving(true);
                const favoritesListId = (favoritesList as any)?.id;

                const process = async (
                    association: 'add' | 'remove',
                    entityIds: number[],
                ) => {
                    if (!entityIds || entityIds.length === 0)
                        return [] as any[];
                    return await sportsApi
                        .url(
                            `/clients/${client.id}/social-benchmarking/favorites/${favoritesListId}/account-groups`,
                        )
                        .json({ association, entityIds })
                        .post()
                        .json();
                };

                await process('remove', toRemove);
                await process('add', toAdd);

                sendNotifications(
                    t('socialBenchmarking.favorites.updatedSuccess'),
                    { context: 'success' },
                );
                await getLeaguesAndFavorites(true as any);
            } catch (e: any) {
                // eslint-disable-next-line no-console
                console.error(e);
                sendNotifications(
                    e?.message ||
                        t('socialBenchmarking.favorites.updateFailed'),
                    { context: 'danger' },
                );
            } finally {
                setIsSaving(false);
            }
        },
        [favoritesList, client, getLeaguesAndFavorites, sendNotifications],
    );

    return (
        <SocialBenchmarkingProvider>
            <div style={{ padding: 24 }}>
                <Flex
                    align="center"
                    justify="space-between"
                    style={{ marginBottom: 24, paddingLeft: 12 }}
                >
                    <Title
                        level={4}
                        style={{
                            margin: 0,
                            color: '#1f1f1f',
                            fontWeight: 400,
                            fontSize: 32,
                        }}
                    >
                        {t('socialBenchmarking.pageTitle')}
                    </Title>
                    <Button
                        color="primary"
                        variant="outlined"
                        onClick={() => setIsFavoritesOpen(true)}
                        icon={<ControlOutlined />}
                    >
                        {t('socialBenchmarking.manageFavorites')}
                    </Button>
                </Flex>
                <SocBenchFilters
                    leagues={leagues}
                    favoritesList={favoritesList}
                    getSocialBenchmarkingAccountGroups={
                        getSocialBenchmarkingAccountGroups
                    }
                    isLoading={isLoading}
                />
                <SocBenchHighlightsContainer
                    accountGroups={accountGroups}
                    highlights={highlights}
                >
                    <SocBenchHighlights
                        highlights={highlights}
                        isLoading={isLoading}
                    />
                </SocBenchHighlightsContainer>
                <SocialBenchmarkingChart
                    socialBenchmarking={socialBenchmarking}
                    sendNotifications={sendNotifications}
                />
                <SocialBenchmarkingTable
                    socialBenchmarking={socialBenchmarking}
                    sendNotifications={sendNotifications}
                    isLoading={isLoading}
                    user={user}
                />
                <SocialBenchmarkingFavorites
                    open={isFavoritesOpen}
                    onClose={() => setIsFavoritesOpen(false)}
                    onSaveChanges={handleSaveFavorites}
                    loading={isSaving}
                    leagues={leagues as any}
                    favoritesList={favoritesList as any}
                />
            </div>
        </SocialBenchmarkingProvider>
    );
};

export default connect(
    ['socialBenchmarking', 'user', 'client'],
    audienceActions,
)(SocialBenchmarking);
